import React, { useState } from 'react';
import { Gift, Loader2, Sparkles } from 'lucide-react';
import { giftCardCheckout } from '../api';
import { useToast } from '../hooks/use-toast';

const AMOUNTS = [25, 50, 100, 150, 250];

const GiftCards = () => {
  const { toast } = useToast();
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState('');
  const [form, setForm] = useState({ purchaser_email: '', recipient_name: '', recipient_email: '', message: '' });
  const [loading, setLoading] = useState(false);

  const effectiveAmount = custom ? Number(custom) : amount;

  const buy = async () => {
    if (loading) return;
    if (!effectiveAmount || effectiveAmount < 10 || effectiveAmount > 1000) {
      toast({ title: 'Choose an amount', description: 'Gift cards are between $10 and $1000.' });
      return;
    }
    setLoading(true);
    try {
      const res = await giftCardCheckout({ amount: effectiveAmount, origin_url: window.location.origin, ...form });
      if (res.checkout_url) window.location.href = res.checkout_url;
      else throw new Error('No checkout URL');
    } catch (e) {
      setLoading(false);
      toast({ title: 'Something went wrong', description: e?.response?.data?.detail || 'Please try again.' });
    }
  };

  return (
    <div className="min-h-screen py-20 px-5" data-testid="gift-cards-page">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <div className="h-16 w-16 mx-auto rounded-full bg-gradient-to-br from-[#d6197d] to-[#ff4da6] flex items-center justify-center mb-5"><Gift className="h-8 w-8 text-white" /></div>
          <h1 className="font-cinzel text-4xl sm:text-5xl text-gold-gradient">Gift Cards</h1>
          <p className="font-cormorant text-xl text-[#f5ecd9]/70 mt-4 max-w-xl mx-auto">Give the gift of radiance. A Prestige Elements gift card can be redeemed toward any product in our collection.</p>
        </div>

        <div className="card-glass rounded-3xl p-8 md:p-10">
          <label className="font-jost text-xs uppercase tracking-widest text-[#f0d582]">Choose an amount</label>
          <div className="flex flex-wrap gap-3 mt-3">
            {AMOUNTS.map((a) => (
              <button key={a} data-testid={`gift-amount-${a}`} onClick={() => { setAmount(a); setCustom(''); }} className={`px-6 py-3 rounded-full font-cinzel transition-all ${!custom && amount === a ? 'bg-gradient-to-r from-[#d4af37] to-[#f0d582] text-[#0a0708]' : 'border border-[#d4af37]/30 text-[#f5ecd9]/80 hover:border-[#d4af37]/60'}`}>${a}</button>
            ))}
            <div className="flex items-center gap-2 rounded-full border border-[#d4af37]/30 px-4">
              <span className="text-[#f5ecd9]/50 font-cinzel">$</span>
              <input data-testid="gift-amount-custom" type="number" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Custom" className="w-24 bg-transparent py-3 text-sm text-[#f5ecd9] focus:outline-none" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-8">
            <div>
              <label className="font-jost text-xs uppercase tracking-widest text-[#f5ecd9]/50">Your email</label>
              <input data-testid="gift-purchaser-email" type="email" value={form.purchaser_email} onChange={(e) => setForm({ ...form, purchaser_email: e.target.value })} className="mt-1 w-full bg-white/5 border border-[#d4af37]/30 rounded-lg px-4 py-3 text-sm text-[#f5ecd9] focus:outline-none focus:border-[#ff4da6]" />
            </div>
            <div>
              <label className="font-jost text-xs uppercase tracking-widest text-[#f5ecd9]/50">Recipient name</label>
              <input data-testid="gift-recipient-name" value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} className="mt-1 w-full bg-white/5 border border-[#d4af37]/30 rounded-lg px-4 py-3 text-sm text-[#f5ecd9] focus:outline-none focus:border-[#ff4da6]" />
            </div>
            <div>
              <label className="font-jost text-xs uppercase tracking-widest text-[#f5ecd9]/50">Recipient email</label>
              <input data-testid="gift-recipient-email" type="email" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} className="mt-1 w-full bg-white/5 border border-[#d4af37]/30 rounded-lg px-4 py-3 text-sm text-[#f5ecd9] focus:outline-none focus:border-[#ff4da6]" />
            </div>
            <div>
              <label className="font-jost text-xs uppercase tracking-widest text-[#f5ecd9]/50">Message (optional)</label>
              <input data-testid="gift-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 w-full bg-white/5 border border-[#d4af37]/30 rounded-lg px-4 py-3 text-sm text-[#f5ecd9] focus:outline-none focus:border-[#ff4da6]" />
            </div>
          </div>

          <button data-testid="gift-buy-btn" onClick={buy} disabled={loading} className="btn-gold w-full mt-8 flex items-center justify-center gap-2 rounded-full py-4 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] via-[#f0d582] to-[#d4af37] disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {loading ? 'Redirecting…' : `Buy $${effectiveAmount || 0} Gift Card`}
          </button>
          <p className="font-jost text-[11px] text-[#f5ecd9]/40 text-center mt-3">The recipient receives their gift card code by email after payment. Redeem at checkout toward any product.</p>
        </div>
      </div>
    </div>
  );
};

export default GiftCards;
